import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
// importing specifically from the Illustration component to avoid SCSS import errors
import { ErrorIllustration } from 'shared/Illustration';
import GetInTouchButton from './GetInTouchButton';
import { UserContext, User } from 'core/context/UserContextHandler';
import { Link, Stack } from '@expert360/ui';
import styles from './index.module.css';

interface Contents {
  title: string;
  content: string;
  subheading?: string;
}
interface StatusTypes {
  [code: string]: Contents;
}

interface HelpMessageProps {
  status: string;
  match: any;
  user: User | null;
}

const statusTypes: StatusTypes = {
  '500': {
    title: 'Something went wrong',
    content: 'Something went wrong!',
  },
  '404': {
    title: 'Page not found',
    content: "Oops — let's get you back on track",
    subheading: "The page you're looking for isn't available.",
  },
  auth: {
    title: 'Please log in',
    content: 'Please log in to view this page',
  },
  genericFrontendError: {
    title: 'Something went wrong',
    content: 'Something went wrong!',
  },
};

const get404Content = (user: User | null) => {
  const path = window.location.pathname;

  if (!user) {
    return (
      <Stack justify="center" alignItems="stretch" flexGrow={false}>
        <Link theme="buttonPrimary" className={styles.linkPadding}>
          <a href={`/login?next=${path}`}>Sign In</a>
        </Link>
        <GetInTouchButton buttonLinks />
      </Stack>
    );
  }

  const userRole = user.role?.toLowerCase();

  const roleContentMap = {
    expert: () => (
      <Stack justify="center" alignItems="stretch" flexGrow={false}>
        <Link theme="buttonPrimary" className={styles.linkPadding}>
          <a href="/browse">Browse Opportunities</a>
        </Link>
        <GetInTouchButton buttonLinks />
      </Stack>
    ),
    consultant: () => (
      <Stack justify="center" alignItems="stretch" flexGrow={false}>
        <Link theme="buttonPrimary" className={styles.linkPadding}>
          <a href="/browse">Browse Opportunities</a>
        </Link>
        <GetInTouchButton buttonLinks />
      </Stack>
    ),
    client: () => (
      <Stack justify="center" alignItems="stretch" flexGrow={false}>
        <Link theme="buttonPrimary" className={styles.linkPadding}>
          <a href="/client">Manage Opportunities</a>
        </Link>
        <GetInTouchButton buttonLinks />
      </Stack>
    ),
    admin: () => null,
    superadmin: () => null,
  };

  const getContentForRole =
    roleContentMap[userRole as keyof typeof roleContentMap];
  return getContentForRole ? getContentForRole() : null;
};

const HelpMessage = ({ status, match = null, user }: HelpMessageProps) => {
  if (status === 'auth' && match) {
    return (
      <>
        Please <a href={`/login?next=${match.url}`}>log in</a> to view this
        page.
      </>
    );
  }

  if (status === '404') {
    return get404Content(user);
  }

  return (
    <>
      Try going back to our <a href="/">homepage</a>, <br /> or{' '}
      <span className="u-display-block-from-745">
        please <GetInTouchButton /> with the Expert360 team if you need help.
      </span>
    </>
  );
};

const ErrorPage = ({ status = '500', match = null }) => {
  const user = useContext(UserContext);
  const { title, content, subheading } = statusTypes[status];
  return (
    <div className="u-s-mb-large">
      <div className="l-container l-container--slim">
        <div className="l-strap">
          <div className="l-container l-container--narrow">
            <Helmet>
              <title>{title} - Expert360</title>
              <meta name="robots" content="noindex" />
            </Helmet>

            <div className="u-text-align-center">
              <h1 className="heading heading--size-large u-s-mb-small">
                {content}
              </h1>
              {subheading && <p className="u-s-mb-small">{subheading}</p>}
              <div className="u-s-mb-large">
                <ErrorIllustration />
              </div>
              <p>
                <HelpMessage status={status} match={match} user={user} />
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ErrorPage.propTypes = {
  status: PropTypes.string,
};

export default ErrorPage;
